import { useCallback, useSyncExternalStore } from 'react';
import {
  ACTIVE_RUN_STATUSES,
  cancelRun,
  createRun,
  getRun,
  runEventsUrl,
  streamAuthorization,
  type Citation,
  type CreateRunRequest,
  type CreateRunResult,
  type RunDetails,
  type RunError,
} from '@/api/runs';
import { handleLogout, OWN_RUNS_PREFIX } from '@/api/client';
import { currentUserKey } from '@/utils/session';
import { currentUserId } from './runOwner';
import { readRunStream, type RunEvent, type StreamDeps } from './runStream';

/**
 * Every chat run the app follows, across Q&As (spec §3 and §5, contract
 * chat-runs-api.md). The store owns the event streams, so a run keeps
 * streaming when the user opens another Q&A, and it marks that Q&A New when
 * the answer completes there. Components read one run with useRun(runId)
 * and the per-Q&A badges with useRunStore().
 */

export type RunPhase =
  | 'queued'
  | 'running'
  | 'stopping'
  | 'completed'
  | 'stopped'
  | 'failed'
  /** The connection was lost and could not be restored. */
  | 'disconnected'
  /** As disconnected, for a run picked up again after a reload. */
  | 'interrupted';

export const isActivePhase = (phase: RunPhase | null | undefined) =>
  phase === 'queued' || phase === 'running' || phase === 'stopping';

/** The server said how the run ended (as opposed to a lost connection). */
const isFinalPhase = (phase: RunPhase) =>
  phase === 'completed' || phase === 'stopped' || phase === 'failed';

/** Where a Stop stands. */
export type StopState =
  | 'none'
  /** Pressed; the cancel request is on its way. Rendering has stopped. */
  | 'requested'
  /** The server confirmed the stop. */
  | 'acknowledged'
  /** The server recorded it but has not stopped the run yet (§7). */
  | 'unconfirmed'
  /** The cancel request failed; the stop is not confirmed. */
  | 'failed';

export interface StageDetail {
  count?: number;
  done?: number;
  total?: number;
}

export interface RunState {
  runId: string;
  inquiryId: string;
  messageId: number | null;
  mode: string | null;
  phase: RunPhase;
  stage: string | null;
  detail: StageDetail | null;
  /** Every delta so far, concatenated. */
  text: string;
  lastSeq: number;
  citations: Citation[] | null;
  error: RunError | null;
  /** Epoch ms the run started (for the elapsed seconds). */
  startedAt: number;
  durationMs: number | null;
  stop: StopState;
  /** Picked up from the server (after a reload) rather than started here. */
  resumed: boolean;
  /** The connection dropped and is being re-established. */
  reconnecting: boolean;
  /**
   * The user's own run: its `user_id` is the signed-in user's (GET
   * /llm/me/info), on any device; until either id is known, started in this
   * browser (remembered across reloads until it ends or the user signs out).
   * Only own runs are stopped at sign-out and mark a Q&A New; colleagues'
   * runs (organization view) are only watched.
   */
  own: boolean;
  /** Who started the run (`user_id`), when known. */
  userId: number | null;
  /**
   * Stop froze the text on screen while the run was being received. A run
   * stopped while lost, or before its pick-up finished, shows the stored
   * answer instead once the list is loaded again.
   */
  textFrozen: boolean;
  /**
   * Disconnected or interrupted while the server still reported the run
   * active: the Q&A stays busy and Stop still cancels it.
   */
  stillRunning: boolean;
}

/** Per-Q&A state (the Generating and New badges of prompt 12). */
export interface RunEntry {
  generating: boolean;
  /** An answer finished while another Q&A was open. */
  unread: boolean;
  /** The active run's id when known. */
  runId: string | null;
}

export type SubmitResult = CreateRunResult | { kind: 'busy' };

export type FinishedListener = (run: RunState, unread: boolean) => void;

export interface RunStoreDeps {
  createRun: typeof createRun;
  getRun: typeof getRun;
  cancelRun: typeof cancelRun;
  stream: StreamDeps;
  /** Resolves after `ms`, or early when `signal` aborts. */
  sleep: (ms: number, signal: AbortSignal) => Promise<void>;
  now: () => number;
  /** Consecutive failed reconnections before a run counts as disconnected. */
  maxReconnects: number;
  /** Wait before resending a POST that got no answer (same idempotency key). */
  resendDelayMs: number;
  /** Reconnection delay until the server sends its `retry:` hint. */
  defaultRetryMs: number;
  /** Attempts to read a run that GET /runs/{id} could not answer (not 404). */
  attachAttempts: number;
  /** The stream answered 401: the session has expired. */
  onUnauthorized: () => void;
  /** Where the ids of own runs that have not ended are remembered. */
  ownRuns: { load: () => string[]; save: (runIds: string[]) => void };
  /**
   * Q&As where a POST got no answer (or a proxy's 5xx), with when: a run
   * created there around that time is taken as this browser's, also after
   * a reload.
   */
  uncertainSends: {
    load: () => Record<string, number>;
    save: (sends: Record<string, number>) => void;
  };
  /** The signed-in user's id (GET /llm/me/info), null while unknown. */
  userId: () => number | null;
}

/**
 * localStorage key of the signed-in user's own unfinished runs, shared by
 * every tab of the browser (read again on each use). Sign-out clears it; a
 * session expiry keeps it (api/client.ts handleLogout).
 */
export const ownRunsKey = () =>
  `${OWN_RUNS_PREFIX}v1:${currentUserKey() ?? 'anonymous'}`;

/** localStorage key of the POSTs without an answer (kept with the own runs). */
export const uncertainSendsKey = () =>
  `${OWN_RUNS_PREFIX}uncertain.v1:${currentUserKey() ?? 'anonymous'}`;

/** How long a POST without an answer may claim a run created after it. */
const UNCERTAIN_SEND_MS = 120_000;

const storedUncertainSends = {
  load: (): Record<string, number> => {
    try {
      const value = JSON.parse(
        localStorage.getItem(uncertainSendsKey()) ?? '{}',
      );
      return value && typeof value === 'object' && !Array.isArray(value)
        ? Object.fromEntries(
            Object.entries(value).filter(
              (entry): entry is [string, number] =>
                typeof entry[1] === 'number',
            ),
          )
        : {};
    } catch {
      return {};
    }
  },
  save: (sends: Record<string, number>) => {
    try {
      if (Object.keys(sends).length === 0) {
        localStorage.removeItem(uncertainSendsKey());
      } else {
        localStorage.setItem(uncertainSendsKey(), JSON.stringify(sends));
      }
    } catch {
      // Storage is optional: the claim then lasts as long as this page.
    }
  },
};

const storedOwnRuns = {
  load: (): string[] => {
    try {
      const value = JSON.parse(localStorage.getItem(ownRunsKey()) ?? '[]');
      return Array.isArray(value) ? value.map(String) : [];
    } catch {
      return [];
    }
  },
  save: (runIds: string[]) => {
    try {
      localStorage.setItem(ownRunsKey(), JSON.stringify(runIds.slice(-50)));
    } catch {
      // Storage is optional: ownership then lasts as long as this page.
    }
  },
};

type Listener = () => void;

const IDLE: RunEntry = { generating: false, unread: false, runId: null };

const MAX_RETRY_MS = 30_000;

const phaseOf = (status: string): RunPhase => {
  switch (status) {
    case 'queued':
    case 'running':
    case 'stopping':
    case 'completed':
    case 'stopped':
    case 'failed':
      return status;
    default:
      return 'running';
  }
};

const sleep = (ms: number, signal: AbortSignal) =>
  new Promise<void>(resolve => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener('abort', done);
      resolve();
    }
    signal.addEventListener('abort', done);
  });

const defaultDeps = (): RunStoreDeps => ({
  createRun,
  getRun,
  cancelRun,
  stream: {
    fetch: (...args) => fetch(...args),
    authorization: streamAuthorization,
    url: runEventsUrl,
  },
  sleep,
  now: () => Date.now(),
  maxReconnects: 6,
  resendDelayMs: 1000,
  // The server's hint (§4: `retry: 3000`) until one arrives.
  defaultRetryMs: 3000,
  attachAttempts: 3,
  onUnauthorized: () => handleLogout('expired'),
  ownRuns: storedOwnRuns,
  uncertainSends: storedUncertainSends,
  userId: currentUserId,
});

/** Performance marks for the display-lag check (first delta → first paint). */
export const runMarks = {
  firstDelta: (runId: string) => `qa-run:${runId}:first-delta`,
  firstPaint: (runId: string) => `qa-run:${runId}:first-paint`,
};

const mark = (name: string) => {
  try {
    if (performance.getEntriesByName(name).length === 0) {
      performance.mark(name);
    }
  } catch {
    // performance.mark is optional.
  }
};

interface Controller {
  abort: AbortController;
  /** Stop was pressed: only `complete` (and `error`) still count. */
  frozen: boolean;
  retryMs: number;
}

export class RunStore {
  private readonly deps: RunStoreDeps;
  private runs = new Map<string, RunState>();
  private controllers = new Map<string, Controller>();
  /** inquiry id → its active run id. */
  private activeRuns = new Map<string, string>();
  /** Q&As with a POST /runs on its way. */
  private creating = new Set<string>();
  /** Those POSTs, so sign-out can stop the runs they create. */
  private sending = new Set<Promise<unknown>>();
  /**
   * Runs the latest Recent Q&As list showed active, with who started them
   * (null for an API without `user_id`), for ownership and sign-out.
   */
  private listed = new Map<string, number | null>();
  /** Runs the server no longer knows (404): not asked about again by itself. */
  private unknown = new Set<string>();
  private unread = new Set<string>();
  private listeners = new Set<Listener>();
  private runListeners = new Map<string, Set<Listener>>();
  private finishedListeners = new Set<FinishedListener>();
  private version = 0;
  private activeInquiryId: string | null = null;

  constructor(deps: Partial<RunStoreDeps> = {}) {
    this.deps = { ...defaultDeps(), ...deps };
  }

  /**
   * Whether the run is the signed-in user's: by user id when both ids are
   * known, else whether this browser started it (and it has not ended),
   * read from storage each time, so every tab agrees.
   */
  isOwn(runId: string) {
    return this.ownedBy(
      runId,
      this.runs.get(runId)?.userId ?? this.listed.get(runId) ?? null,
    );
  }

  private ownedBy(runId: string, owner: number | null) {
    const me = this.deps.userId();
    if (me !== null && owner !== null) return owner === me;
    return this.deps.ownRuns.load().includes(runId);
  }

  /**
   * The runs the Recent Q&As list shows active (chat-runs-api.md §15), with
   * who started them. Replaces what the previous list said.
   */
  noteListed(activeRuns: { id: string; user_id?: number | null }[]) {
    this.listed = new Map(
      activeRuns.map(run => [
        run.id,
        typeof run.user_id === 'number' ? run.user_id : null,
      ]),
    );
  }

  /** Read, change and write back, so other tabs' entries are kept. */
  private setOwn(runId: string, own: boolean) {
    const ids = this.deps.ownRuns.load();
    if (own === ids.includes(runId)) return;
    this.deps.ownRuns.save(
      own ? [...ids, runId] : ids.filter(id => id !== runId),
    );
  }

  // ---- per-Q&A entries ----------------------------------------------------

  subscribe = (listener: Listener) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Changes whenever a per-Q&A entry changes (for useSyncExternalStore). */
  getVersion = () => this.version;

  private emitEntries() {
    this.version++;
    this.listeners.forEach(listener => listener());
  }

  get(inquiryId: string | number | null | undefined): RunEntry {
    if (inquiryId == null) return IDLE;
    const id = String(inquiryId);
    const runId = this.activeRuns.get(id) ?? null;
    const generating = !!runId || this.creating.has(id);
    const unread = this.unread.has(id);
    if (!generating && !unread) return IDLE;
    return { generating, unread, runId };
  }

  /**
   * Q&As with an answer of this user in progress, followed here or on its
   * way (tests check sign-out with it; the dialog counts ownActiveCount).
   */
  generatingIds(): string[] {
    const own = [...this.activeRuns]
      .filter(([, runId]) => this.isOwn(runId))
      .map(([inquiryId]) => inquiryId);
    return [...new Set([...own, ...this.creating])];
  }

  /**
   * The user's runs this page knows to be in progress: followed here
   * (including lost ones the server still had active), listed active in
   * Recent Q&As, or remembered as started in this browser and seen active.
   */
  private ownActiveRunIds(): string[] {
    const ids = new Set<string>();
    for (const run of this.runs.values()) {
      if (
        (isActivePhase(run.phase) || run.stillRunning) &&
        this.isOwn(run.runId)
      ) {
        ids.add(run.runId);
      }
    }
    for (const runId of this.listed.keys()) {
      // Listed active, unless this page has seen it end since.
      const known = this.runs.get(runId);
      const ended =
        !!known && !isActivePhase(known.phase) && !known.stillRunning;
      if (!ended && this.isOwn(runId)) ids.add(runId);
    }
    return [...ids];
  }

  /**
   * How many answers sign-out stops: every own run in progress, in this tab
   * or another (and questions still on their way).
   */
  ownActiveCount(): number {
    const runIds = this.ownActiveRunIds();
    const busy = new Set(
      runIds.map(runId => this.runs.get(runId)?.inquiryId).filter(Boolean),
    );
    return (
      runIds.length + [...this.creating].filter(id => !busy.has(id)).length
    );
  }

  /** The Q&A on screen; opening one clears its New badge. */
  setActive(inquiryId: string | number | null | undefined) {
    this.activeInquiryId = inquiryId == null ? null : String(inquiryId);
    if (this.activeInquiryId && this.unread.delete(this.activeInquiryId)) {
      this.emitEntries();
    }
  }

  /** The Q&A was deleted: stop following its runs. */
  forget(inquiryId: string | number) {
    const id = String(inquiryId);
    for (const run of [...this.runs.values()]) {
      if (run.inquiryId !== id) continue;
      this.controllers.get(run.runId)?.abort.abort();
      this.controllers.delete(run.runId);
      this.runs.delete(run.runId);
      this.emitRun(run.runId);
    }
    this.activeRuns.delete(id);
    this.creating.delete(id);
    this.unread.delete(id);
    this.emitEntries();
  }

  /** Called with every run that ends (complete, stop, failure). */
  onFinished(listener: FinishedListener) {
    this.finishedListeners.add(listener);
    return () => {
      this.finishedListeners.delete(listener);
    };
  }

  // ---- runs ---------------------------------------------------------------

  subscribeRun = (runId: string, listener: Listener) => {
    let set = this.runListeners.get(runId);
    if (!set) {
      set = new Set();
      this.runListeners.set(runId, set);
    }
    set.add(listener);
    return () => {
      set!.delete(listener);
      if (set!.size === 0) this.runListeners.delete(runId);
    };
  };

  getRun(runId: string | null | undefined): RunState | undefined {
    return runId ? this.runs.get(runId) : undefined;
  }

  /** The run being generated in a Q&A, if any. */
  activeRunOf(inquiryId: string | number | null | undefined) {
    if (inquiryId == null) return undefined;
    return this.getRun(this.activeRuns.get(String(inquiryId)));
  }

  private emitRun(runId: string) {
    this.runListeners.get(runId)?.forEach(listener => listener());
  }

  private update(runId: string, patch: Partial<RunState>) {
    const run = this.runs.get(runId);
    if (!run) return;
    this.runs.set(runId, { ...run, ...patch });
    this.emitRun(runId);
  }

  private register(run: RunState) {
    this.runs.set(run.runId, run);
    if (isActivePhase(run.phase)) this.activeRuns.set(run.inquiryId, run.runId);
    this.emitRun(run.runId);
    this.emitEntries();
  }

  /**
   * Send a question (or a retry, alternative or edit). Refused locally while
   * the Q&A has a run or a send on its way; the server refuses a second
   * active run too (409, whose run is then followed). A POST that got no
   * answer is resent once with the same idempotency key, which returns the
   * original run instead of creating another.
   */
  submit(
    inquiryId: string | number,
    body: Omit<CreateRunRequest, 'idempotency_key'>,
    idempotencyKey: string,
  ): Promise<SubmitResult> {
    const id = String(inquiryId);
    if (this.get(id).generating) return Promise.resolve({ kind: 'busy' });
    const pending = this.send(id, body, idempotencyKey);
    this.sending.add(pending);
    void pending.finally(() => this.sending.delete(pending));
    return pending;
  }

  private async send(
    id: string,
    body: Omit<CreateRunRequest, 'idempotency_key'>,
    idempotencyKey: string,
  ): Promise<SubmitResult> {
    this.creating.add(id);
    this.emitEntries();
    try {
      const request = { ...body, idempotency_key: idempotencyKey };
      let result = await this.deps.createRun(request);
      if (result.kind === 'network') {
        await this.deps.sleep(
          this.deps.resendDelayMs,
          new AbortController().signal,
        );
        result = await this.deps.createRun(request);
      }
      // No answer, or a proxy's 5xx: the run may exist all the same.
      if (
        result.kind === 'network' ||
        (result.kind === 'error' && result.status >= 500)
      ) {
        this.setUncertain(id, this.deps.now());
      }
      if (result.kind === 'created') {
        this.creating.delete(id);
        this.setUncertain(id, null);
        this.setOwn(result.runId, true);
        if (!this.runs.has(result.runId)) {
          this.register({
            runId: result.runId,
            inquiryId: id,
            messageId: result.messageId,
            mode: body.mode ?? null,
            phase: phaseOf(result.status),
            stage: null,
            detail: null,
            text: '',
            lastSeq: 0,
            citations: null,
            error: null,
            startedAt: this.deps.now(),
            durationMs: null,
            stop: 'none',
            resumed: false,
            reconnecting: false,
            own: true,
            userId: this.deps.userId(),
            textFrozen: false,
            stillRunning: false,
          });
          this.follow(result.runId);
        }
      } else if (result.kind === 'conflict' && result.activeRunId) {
        this.creating.delete(id);
        void this.attach(result.activeRunId, id);
      }
      return result;
    } finally {
      if (this.creating.delete(id)) this.emitEntries();
    }
  }

  /**
   * Pick up a run from the server (after a reload, a 409 or a reconnect
   * request): render its partial text and continue the stream right after
   * `last_seq`. Returns the server's view of it (null when unknown).
   */
  async attach(
    runId: string,
    inquiryId?: string | number,
  ): Promise<RunDetails | null> {
    const existing = this.runs.get(runId);
    if (existing && isActivePhase(existing.phase)) return null;
    let details: RunDetails | null = null;
    for (let attempt = 1; ; attempt++) {
      const answer = await this.deps.getRun(runId);
      details = answer.run;
      if (answer.status === 404) this.unknown.add(runId);
      if (details || answer.status === 404) break;
      if (attempt >= this.deps.attachAttempts) break;
      await this.deps.sleep(
        this.deps.defaultRetryMs * attempt,
        new AbortController().signal,
      );
    }
    if (!details) return null;
    this.unknown.delete(runId);
    this.claimUncertainSend(runId, details);
    const current = this.runs.get(runId);
    if (current && isActivePhase(current.phase)) return details;
    if (!ACTIVE_RUN_STATUSES.has(details.status)) {
      if (current) this.finishFromDetails(runId, details);
      else this.setOwn(runId, false);
      return details;
    }
    const startedAt = Date.parse(details.started_at ?? details.created_at);
    const userId = typeof details.user_id === 'number' ? details.user_id : null;
    // A Stop pressed before the connection was lost still holds; one that
    // failed does not (the run goes on, and is shown generating again).
    const stopped =
      !!current && current.stop !== 'none' && current.stop !== 'failed';
    this.register({
      runId,
      inquiryId: String(inquiryId ?? current?.inquiryId ?? details.inquiry_id),
      messageId: details.message_id,
      mode: details.mode,
      phase: phaseOf(details.status),
      stage: details.stage,
      detail: null,
      text: stopped
        ? current!.text
        : details.partial_text || (current?.text ?? ''),
      lastSeq: details.last_seq,
      citations: null,
      error: details.error,
      startedAt: Number.isFinite(startedAt) ? startedAt : this.deps.now(),
      durationMs: null,
      stop: stopped
        ? current!.stop
        : details.status === 'stopping'
          ? 'unconfirmed'
          : 'none',
      resumed: !current || current.resumed,
      reconnecting: false,
      own: this.ownedBy(runId, userId),
      userId,
      textFrozen: stopped && current!.textFrozen,
      stillRunning: false,
    });
    this.follow(runId);
    return details;
  }

  /** Try again to reach a disconnected or interrupted run. Never resubmits. */
  reconnect(runId: string) {
    const run = this.runs.get(runId);
    if (!run || isActivePhase(run.phase)) return Promise.resolve(null);
    return this.attach(runId, run.inquiryId);
  }

  /**
   * Stop: rendering stops at once (later deltas are ignored), then the
   * server is asked to cancel. The state says whether it confirmed.
   */
  async stop(runId: string, inquiryId?: string | number) {
    let run = this.runs.get(runId);
    if (!run) {
      // Listed as active but not followed here (its pick-up failed): follow
      // it from here on, stopped, so its end is shown.
      if (inquiryId == null) return;
      this.register({
        runId,
        inquiryId: String(inquiryId),
        messageId: null,
        mode: null,
        phase: 'running',
        stage: null,
        detail: null,
        text: '',
        lastSeq: 0,
        citations: null,
        error: null,
        startedAt: this.deps.now(),
        durationMs: null,
        stop: 'none',
        resumed: true,
        reconnecting: false,
        own: this.isOwn(runId),
        userId: this.listed.get(runId) ?? null,
        textFrozen: false,
        stillRunning: false,
      });
      run = this.runs.get(runId)!;
    }
    const lost = !isActivePhase(run.phase) && run.stillRunning;
    if (!isActivePhase(run.phase) && !lost) return;
    const controller = this.controllers.get(runId);
    if (controller) controller.frozen = true;
    // Only text that was arriving is frozen; a lost run, or one whose
    // pick-up has not delivered anything, shows what the server stored.
    const textFrozen = !!controller && !lost;
    this.update(
      runId,
      lost
        ? { stop: 'requested', textFrozen }
        : { phase: 'stopping', stop: 'requested', textFrozen },
    );

    const response = await this.deps.cancelRun(runId);
    const now = this.runs.get(runId);
    if (!now || isFinalPhase(now.phase)) return;
    if (response.failed) {
      this.update(runId, { stop: 'failed' });
      // A run registered by this Stop has no stream yet: follow it, so its
      // end is seen even if Stop is never pressed again.
      if (!lost) this.follow(runId);
      return;
    }
    switch (response.status) {
      case 'stopped':
        if (response.acknowledged) {
          this.finish(runId, 'stopped', {});
          return;
        }
        this.update(runId, { stop: 'unconfirmed' });
        break;
      case 'completed':
      case 'failed':
        // The run had finished first (the stream's `complete` says the
        // same): read how, so a failure shows the server's error.
        await this.settle(runId, response.status);
        return;
      default:
        this.update(runId, { stop: 'unconfirmed' });
    }
    // Not confirmed yet: make sure something follows the run to its end
    // (a lost or newly registered run has no stream).
    if (lost) void this.attach(runId);
    else this.follow(runId);
  }

  /** End a run with the server's record of it (error, duration, message). */
  private async settle(runId: string, status: string) {
    const { run: details } = await this.deps.getRun(runId);
    if (details && !ACTIVE_RUN_STATUSES.has(details.status)) {
      this.finishFromDetails(runId, details);
    } else {
      this.finish(runId, status, {});
    }
  }

  /** Close every stream without stopping the runs (tests, teardown). */
  dispose() {
    for (const controller of this.controllers.values()) {
      controller.abort.abort();
    }
    this.controllers.clear();
  }

  /**
   * A lost run (disconnected or interrupted) may have ended meanwhile: ask
   * the server again (on focus, when the network is back, or when the list
   * no longer shows it active). Never resubmits.
   */
  recheckLost() {
    for (const run of this.runs.values()) {
      // The server no longer knows it (its Q&A was deleted): Reconnect can
      // still ask, but nothing asks again by itself.
      if (this.unknown.has(run.runId)) continue;
      if (run.phase === 'disconnected' || run.phase === 'interrupted') {
        void this.reconnect(run.runId);
      }
    }
  }

  /**
   * Stop the user's active runs (sign-out): those followed or listed here,
   * those of questions still on their way to the server, and those started
   * in this browser before a reload that are not followed (yet). Gives up
   * waiting after `capMs`.
   */
  async stopAll(capMs = 5_000) {
    const work = (async () => {
      await Promise.allSettled([...this.sending]);
      const runIds = new Set([
        ...this.ownActiveRunIds(),
        ...this.deps.ownRuns.load(),
      ]);
      await Promise.all(
        [...runIds].map(runId => {
          const run = this.runs.get(runId);
          if (!run) {
            // Cancel it anyway; a run that has ended answers with its
            // final status.
            return this.deps.cancelRun(runId);
          }
          return this.stop(runId);
        }),
      );
    })();
    await Promise.race([
      work,
      this.deps.sleep(capMs, new AbortController().signal),
    ]);
  }

  /** Remember (or forget, with null) a POST in a Q&A that got no answer. */
  private setUncertain(inquiryId: string, sentAt: number | null) {
    const now = this.deps.now();
    const sends = Object.fromEntries(
      Object.entries(this.deps.uncertainSends.load()).filter(
        ([id, at]) => id !== inquiryId && now - at < UNCERTAIN_SEND_MS,
      ),
    );
    if (sentAt !== null) sends[inquiryId] = sentAt;
    this.deps.uncertainSends.save(sends);
  }

  /** A run created in a Q&A right after a POST there got no answer. */
  private claimUncertainSend(runId: string, details: RunDetails) {
    const inquiryId = String(details.inquiry_id);
    const sentAt = this.deps.uncertainSends.load()[inquiryId];
    if (sentAt === undefined) return;
    if (this.deps.now() - sentAt >= UNCERTAIN_SEND_MS) {
      this.setUncertain(inquiryId, null);
      return;
    }
    if (this.isOwn(runId)) {
      this.setUncertain(inquiryId, null);
      return;
    }
    const createdAt = Date.parse(details.created_at);
    // Wide window: the server's clock and this one may differ.
    if (
      Number.isFinite(createdAt) &&
      Math.abs(createdAt - sentAt) < UNCERTAIN_SEND_MS
    ) {
      this.setOwn(runId, true);
      this.setUncertain(inquiryId, null);
    }
  }

  private follow(runId: string) {
    if (this.controllers.has(runId)) return;
    const controller: Controller = {
      abort: new AbortController(),
      frozen: false,
      retryMs: this.deps.defaultRetryMs,
    };
    const run = this.runs.get(runId);
    if (run?.stop !== 'none' && run?.stop !== undefined) {
      controller.frozen = true;
    }
    this.controllers.set(runId, controller);
    void this.pump(runId, controller);
  }

  private async pump(runId: string, controller: Controller) {
    const { signal } = controller.abort;
    let failures = 0;
    try {
      while (!signal.aborted) {
        const run = this.runs.get(runId);
        if (!run || !isActivePhase(run.phase)) return;
        let received = false;
        let opened = false;
        const end = await readRunStream(this.deps.stream, {
          runId,
          lastSeq: run.lastSeq,
          signal,
          onRetryHint: ms => {
            controller.retryMs = ms;
          },
          onOpen: () => {
            opened = true;
            if (this.runs.get(runId)?.reconnecting) {
              this.update(runId, { reconnecting: false });
            }
          },
          onEvent: event => {
            received = true;
            this.apply(runId, event, controller);
          },
        });
        if (signal.aborted || end.kind === 'aborted') return;
        if (!isActivePhase(this.runs.get(runId)?.phase)) return;

        // Closed without `complete`: the run may have ended without one
        // (worker crash, expired log) or the connection dropped.
        if (end.kind === 'http' && end.status === 401) {
          this.lose(runId, false);
          this.deps.onUnauthorized();
          return;
        }
        const { status, run: details } = await this.deps.getRun(runId);
        if (signal.aborted) return;
        if (details && !ACTIVE_RUN_STATUSES.has(details.status)) {
          this.finishFromDetails(runId, details);
          return;
        }
        if (status === 404) {
          this.unknown.add(runId);
          this.lose(runId, false);
          return;
        }
        const dropped = end.kind !== 'ended';
        // A stream the server closed after it was open (its 10-minute limit,
        // heartbeats only during a long stage) is not a failure.
        if (received || (opened && !dropped)) {
          failures = dropped ? 1 : 0;
        } else {
          failures += 1;
        }
        if (failures > this.deps.maxReconnects) {
          // Unless the server said the run is gone, assume it still runs
          // (the GET may have failed for the same reason as the stream).
          this.lose(runId, status !== 404);
          return;
        }
        if (dropped || failures > 0) {
          this.update(runId, { reconnecting: true });
        }
        const delay = Math.min(
          controller.retryMs * 2 ** Math.max(0, failures - 1),
          MAX_RETRY_MS,
        );
        await this.deps.sleep(failures === 0 && received ? 0 : delay, signal);
      }
    } finally {
      if (this.controllers.get(runId) === controller) {
        this.controllers.delete(runId);
      }
    }
  }

  private apply(runId: string, event: RunEvent, controller: Controller) {
    const run = this.runs.get(runId);
    if (!run || !isActivePhase(run.phase)) return; // late event
    if (event.seq <= run.lastSeq) return; // already processed
    const patch: Partial<RunState> = { lastSeq: event.seq };
    const frozen = controller.frozen;
    const data = event.data;

    switch (event.type) {
      case 'stage': {
        if (frozen) break;
        const stage = typeof data.stage === 'string' ? data.stage : null;
        const detail: StageDetail | null =
          data.detail && typeof data.detail === 'object' ? data.detail : null;
        if (
          stage === 'cross_checking' &&
          run.stage === 'cross_checking' &&
          detail &&
          run.detail
        ) {
          // Per-file progress arrives from several workers: keep the highest.
          patch.detail = {
            ...detail,
            done: Math.max(detail.done ?? 0, run.detail.done ?? 0),
            total: detail.total ?? run.detail.total,
          };
        } else {
          patch.detail = detail;
        }
        patch.stage = stage;
        if (stage && stage !== 'waiting' && run.phase === 'queued') {
          patch.phase = 'running';
        }
        break;
      }
      case 'delta': {
        if (frozen || typeof data.text !== 'string') break;
        if (run.text === '' && data.text !== '') {
          mark(runMarks.firstDelta(runId));
        }
        patch.text = run.text + data.text;
        if (run.phase === 'queued') patch.phase = 'running';
        break;
      }
      case 'citations':
        if (!frozen && Array.isArray(data.items)) patch.citations = data.items;
        break;
      case 'error':
        patch.error = {
          code: String(data.code ?? 'internal_error'),
          message: String(data.message ?? ''),
          retryable: data.retryable === true,
        };
        break;
      case 'complete': {
        this.update(runId, patch);
        this.finish(runId, String(data.status ?? 'failed'), {
          durationMs:
            typeof data.duration_ms === 'number' ? data.duration_ms : null,
          messageId:
            data.message_id === null || data.message_id === undefined
              ? null
              : Number(data.message_id),
        });
        return;
      }
      default:
        break;
    }
    this.update(runId, patch);
  }

  private finishFromDetails(runId: string, details: RunDetails) {
    this.update(runId, {
      error: details.error ?? this.runs.get(runId)?.error ?? null,
    });
    this.finish(runId, details.status, {
      durationMs: details.duration_ms,
      messageId: details.message_id,
    });
  }

  /** The run ended; nothing it sends afterwards is shown. */
  private finish(
    runId: string,
    status: string,
    extra: { durationMs?: number | null; messageId?: number | null },
  ) {
    const run = this.runs.get(runId);
    if (!run || isFinalPhase(run.phase)) return;
    const phase: RunPhase =
      status === 'completed' || status === 'stopped' ? status : 'failed';
    const patch: Partial<RunState> = {
      phase,
      reconnecting: false,
      stillRunning: false,
      stop:
        phase === 'stopped' && run.stop !== 'none' ? 'acknowledged' : run.stop,
    };
    if (extra.durationMs !== undefined) patch.durationMs = extra.durationMs;
    if (extra.messageId !== undefined) patch.messageId = extra.messageId;
    this.update(runId, patch);
    const own = run.own || this.isOwn(runId);
    this.setOwn(runId, false);
    this.controllers.get(runId)?.abort.abort();
    this.controllers.delete(runId);
    if (this.activeRuns.get(run.inquiryId) === runId) {
      this.activeRuns.delete(run.inquiryId);
    }
    const unread =
      phase === 'completed' && own && run.inquiryId !== this.activeInquiryId;
    if (unread) this.unread.add(run.inquiryId);
    this.emitEntries();
    const finished = this.runs.get(runId)!;
    this.finishedListeners.forEach(listener => listener(finished, unread));
  }

  /**
   * The connection could not be restored: keep the partial text. While the
   * server still reports the run active the Q&A stays busy (no second run)
   * and Stop still cancels it; Reconnect picks it up again.
   */
  private lose(runId: string, serverActive: boolean) {
    const run = this.runs.get(runId);
    if (!run || !isActivePhase(run.phase)) return;
    this.update(runId, {
      phase: run.resumed ? 'interrupted' : 'disconnected',
      reconnecting: false,
      stillRunning: serverActive,
    });
    this.controllers.get(runId)?.abort.abort();
    this.controllers.delete(runId);
    if (!serverActive && this.activeRuns.get(run.inquiryId) === runId) {
      this.activeRuns.delete(run.inquiryId);
    }
    this.emitEntries();
  }
}

export const runStore = new RunStore();

/** Re-render when a per-Q&A entry changes; read entries with runStore.get(). */
export const useRunStore = () => {
  useSyncExternalStore(runStore.subscribe, runStore.getVersion);
  return runStore;
};

/** One run, re-rendering on each of its changes. */
export const useRun = (runId: string | null | undefined, store = runStore) => {
  const subscribe = useCallback(
    (listener: () => void) =>
      runId ? store.subscribeRun(runId, listener) : () => undefined,
    [runId, store],
  );
  return useSyncExternalStore(subscribe, () => store.getRun(runId));
};
